import {
  useLocalParticipant,
  useMediaDevices,
  usePersistentUserChoices,
  useRoomContext,
} from "@livekit/components-react"
import { Box } from "@mui/material"
import {
  ChatCircleText,
  Gear,
  Microphone,
  MicrophoneSlash,
  PhoneSlash,
  VideoCamera,
  VideoCameraSlash,
} from "@phosphor-icons/react"
import { Track } from "livekit-client"
import React, { useState } from "react"
import { useAppContext, useAppDispatchContext } from "../../app/Context"
import { DeviceOption } from "../../types/Common"
import { StateAction } from "../../types/State"
import { DeviceSelectionDialog } from "../MediaControlsBar/DeviceSelectionDialog"

interface CustomControlsBarProps {
  showChat: boolean
  toggleChat: () => void
  disconnect: () => void
  audioState: boolean
  videoState: boolean
}

interface CallActionButtonProps {
  label: string
  onClick: () => void
  active?: boolean
  testId?: string
  children: React.ReactNode
}

function CallActionButton({
  label,
  onClick,
  active = false,
  testId,
  children,
}: CallActionButtonProps) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      data-testid={testId}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--spacing-tokens-size-in-px-spacing-spacing-lg)",
        border: "none",
        borderRadius: "var(--spacing-tokens-size-in-px-radius-radius-full)",
        backgroundColor: active
          ? "var(--call-chip-bg-active)"
          : "var(--call-chip-bg)",
        color: active
          ? "var(--color-foreground-fg-brand-primary)"
          : "var(--color-foreground-fg-white)",
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        "&:hover": { backgroundColor: "var(--call-chip-bg-hover)" },
      }}
    >
      {children}
    </Box>
  )
}

const ICON_SIZE = 28

export function CustomControlsBar({
  showChat,
  toggleChat,
  disconnect,
  audioState,
  videoState,
}: CustomControlsBarProps) {
  const [isMicEnabled, setIsMicEnabled] = useState<boolean>(audioState)
  const [isCameraEnabled, setIsCameraEnabled] = useState<boolean>(videoState)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [audioInputs, setAudioInputs] = useState<DeviceOption[]>([])
  const [videoInputs, setVideoInputs] = useState<DeviceOption[]>([])
  const [hasAppliedInitialDevices, setHasAppliedInitialDevices] =
    useState(false)

  const context = useAppContext()
  const { saveAudioInputDeviceId, saveVideoInputDeviceId } =
    usePersistentUserChoices({})

  const selectedAudioInput = context.audioDevice || ""
  const selectedVideoInput = context.videoDevice || ""

  const { localParticipant } = useLocalParticipant()
  const room = useRoomContext()
  const dispatch = useAppDispatchContext()

  const availableAudioInputs = useMediaDevices({ kind: "audioinput" })
  const availableVideoInputs = useMediaDevices({ kind: "videoinput" })

  React.useEffect(() => {
    if (availableAudioInputs.length > 0) {
      setAudioInputs(
        availableAudioInputs.map((device) => ({
          label: device.label,
          deviceId: device.deviceId,
        })),
      )
      if (!selectedAudioInput) {
        const audioInput = availableAudioInputs[0].deviceId
        dispatch({ type: StateAction.setAudioDevice, payload: audioInput })
        saveAudioInputDeviceId(audioInput)
      }
    }

    if (availableVideoInputs.length > 0) {
      setVideoInputs(
        availableVideoInputs.map((device) => ({
          label: device.label,
          deviceId: device.deviceId,
        })),
      )
      if (!selectedVideoInput) {
        const videoInput = availableVideoInputs[0].deviceId
        dispatch({ type: StateAction.setVideoDevice, payload: videoInput })
        saveVideoInputDeviceId(videoInput)
      }
    }
  }, [
    availableAudioInputs,
    availableVideoInputs,
    selectedAudioInput,
    selectedVideoInput,
    dispatch,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  ])

  React.useEffect(() => {
    if (
      localParticipant &&
      room &&
      selectedAudioInput &&
      !hasAppliedInitialDevices
    ) {
      const audioTrack = localParticipant.getTrackPublication(
        Track.Source.Microphone,
      )?.track
      if (audioTrack) {
        audioTrack
          .setDeviceId({ exact: selectedAudioInput })
          .then(() => {
            setHasAppliedInitialDevices(true)
          })
          .catch((e) => {
            console.error("Failed to apply initial audio device:", e)
            setHasAppliedInitialDevices(true)
          })
      }
    }
  }, [localParticipant, room, selectedAudioInput, hasAppliedInitialDevices])

  const toggleMicrophone = async () => {
    if (!localParticipant) return
    try {
      const enabled = !isMicEnabled
      await localParticipant.setMicrophoneEnabled(enabled)
      setIsMicEnabled(enabled)
    } catch (e) {
      console.error("Failed to toggle microphone:", e)
    }
  }

  const toggleCamera = async () => {
    if (!localParticipant) return
    try {
      const enabled = !isCameraEnabled
      await localParticipant.setCameraEnabled(enabled)
      setIsCameraEnabled(enabled)
    } catch (e) {
      console.error("Failed to toggle camera:", e)
    }
  }

  const handleSettingsClick = () => setSettingsOpen(true)
  const handleSettingsClose = () => setSettingsOpen(false)

  const handleAudioInputChange = async (deviceId: string) => {
    if (!localParticipant || !room) return
    try {
      dispatch({ type: StateAction.setAudioDevice, payload: deviceId })
      saveAudioInputDeviceId(deviceId)

      const audioTrack = localParticipant.getTrackPublication(
        Track.Source.Microphone,
      )?.track
      if (audioTrack) {
        await audioTrack.setDeviceId({ exact: deviceId })
      } else {
        await room.switchActiveDevice("audioinput", deviceId, true)
      }
    } catch (e) {
      console.error("Failed to switch audio device:", e)
    }
  }

  const handleVideoInputChange = async (deviceId: string) => {
    if (!localParticipant || !room) return
    try {
      dispatch({ type: StateAction.setVideoDevice, payload: deviceId })
      saveVideoInputDeviceId(deviceId)

      const videoTrack = localParticipant.getTrackPublication(
        Track.Source.Camera,
      )?.track
      if (videoTrack) {
        await localParticipant.setCameraEnabled(false)
        await videoTrack.setDeviceId({ exact: deviceId })
        await localParticipant.setCameraEnabled(true)
        setIsCameraEnabled(true)
      } else {
        await room.switchActiveDevice("videoinput", deviceId, true)
      }
    } catch (e) {
      console.error("Failed to switch video device:", e)
    }
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          position: "absolute",
          bottom: "var(--spacing-tokens-size-in-px-spacing-spacing-2xl)",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 6,
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing-tokens-size-in-px-spacing-spacing-md)",
        }}
      >
        <CallActionButton
          label={isCameraEnabled ? "Turn camera off" : "Turn camera on"}
          onClick={toggleCamera}
          active={!isCameraEnabled}
          testId="camera-button"
        >
          {isCameraEnabled ? (
            <VideoCamera size={ICON_SIZE} />
          ) : (
            <VideoCameraSlash size={ICON_SIZE} />
          )}
        </CallActionButton>

        <CallActionButton
          label={isMicEnabled ? "Mute microphone" : "Unmute microphone"}
          onClick={toggleMicrophone}
          active={!isMicEnabled}
          testId="microphone-button"
        >
          {isMicEnabled ? (
            <Microphone size={ICON_SIZE} />
          ) : (
            <MicrophoneSlash size={ICON_SIZE} />
          )}
        </CallActionButton>

        <CallActionButton
          label={showChat ? "Hide conversation" : "Show conversation"}
          onClick={toggleChat}
          active={showChat}
          testId="chat-button"
        >
          <ChatCircleText size={ICON_SIZE} />
        </CallActionButton>

        <CallActionButton label="Device settings" onClick={handleSettingsClick}>
          <Gear size={ICON_SIZE} />
        </CallActionButton>

        <Box
          component="button"
          type="button"
          aria-label="Leave call"
          onClick={disconnect}
          data-testid="end-call-button"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 52,
            height: 52,
            border: "none",
            borderRadius: "var(--spacing-tokens-size-in-px-radius-radius-full)",
            backgroundColor: "var(--call-hangup-bg)",
            color: "var(--color-foreground-fg-white)",
            cursor: "pointer",
            transition: "filter 0.2s ease",
            "&:hover": { filter: "brightness(0.92)" },
          }}
        >
          <PhoneSlash size={ICON_SIZE} weight="fill" />
        </Box>
      </Box>

      <DeviceSelectionDialog
        open={settingsOpen}
        onClose={handleSettingsClose}
        audioInputs={audioInputs}
        videoInputs={videoInputs}
        selectedAudioInput={selectedAudioInput}
        selectedVideoInput={selectedVideoInput}
        handleAudioInputChange={handleAudioInputChange}
        handleVideoInputChange={handleVideoInputChange}
      />
    </React.Fragment>
  )
}
